import React, { useEffect, useState, useRef, Suspense } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { SendHorizontal, Check, CheckCheck, Loader2, FileText, X, Paperclip, SmilePlus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { sendMessage, Message } from '@/services/messageService';
import { useQueryClient } from '@tanstack/react-query';
import { createDebouncedTypingIndicator, subscribeToTypingIndicators } from '@/services/typingService';
import { formatMessageTime } from '@/utils/dateUtils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar';
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));
import { EmojiClickData } from 'emoji-picker-react';

interface MessageThreadProps {
  conversationId: string;
}

interface TypingUser {
  user_id: string;
  is_typing: boolean;
}

export const MessageThread: React.FC<MessageThreadProps> = ({ conversationId }) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [typingIndicator, setTypingIndicator] = useState<() => void | null>(null);
  const [conversationData, setConversationData] = useState<{organization_id: string} | null>(null);
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Set up typing indicators
  useEffect(() => {
    if (!conversationId || !user) return;
    
    // Create a debounced typing indicator for this user
    const debouncedTypingIndicator = createDebouncedTypingIndicator(
      conversationId,
      user.id
    );
    
    setTypingIndicator(() => debouncedTypingIndicator);
    
    // Subscribe to typing indicators from other users
    const subscription = subscribeToTypingIndicators(
      conversationId,
      (users) => {
        // Filter out current user from typing indicators
        const otherTypingUsers = users.filter(typingUser => typingUser.user_id !== user.id);
        setTypingUsers(otherTypingUsers);
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId, user]);

  useEffect(() => {
    if (!conversationId) return;
    
    loadMessages();
    
    // Set up real-time subscription for new messages
    const channel = supabase
      .channel(`messages-${conversationId}`)
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
        async (payload) => {
          const newMessage = payload.new as any;
          
          // Process the new message with sender information
          let senderInfo = null;
          if (newMessage.sender_type === 'parent') {
            const { data: parentData, error: parentError } = await supabase
              .from('parents')
              .select('first_name, last_name')
              .eq('id', newMessage.sender_id)
              .single();

            if (!parentError) {
              senderInfo = parentData;
            }
          } else if (newMessage.sender_type === 'staff') {
            // For staff messages, get organization name
            const { data: orgData, error: orgError } = await supabase
              .from('organizations')
              .select('name')
              .eq('id', conversationData?.organization_id)
              .single();

            if (!orgError) {
              senderInfo = { name: orgData.name };
            }
          }

          const processedMessage = {
            ...newMessage,
            parent: newMessage.sender_type === 'parent' ? senderInfo : null,
            organization: newMessage.sender_type === 'staff' ? senderInfo : null,
            is_read: false,
            read_at: null,
            message_reads: []
          };
          
          // Only append if not already in the list
          setMessages(prev => {
            if (!prev.some(msg => msg.id === newMessage.id)) {
              const updated = [...prev, processedMessage];
              // Also invalidate conversations list to update last message
              queryClient.invalidateQueries({ queryKey: ['conversations'] });
              return updated;
            }
            return prev;
          });
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingUsers]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      // Get conversation's organization_id first
      const { data: convData, error: convError } = await supabase
        .from('conversations')
        .select('organization_id')
        .eq('id', conversationId)
        .single();

      if (convError) {
        console.error('Error fetching conversation:', convError);
        throw convError;
      }
      
      // Save conversation data
      setConversationData(convData);
      
      // Now get messages with message_reads
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select(`
          *,
          message_reads (
            user_id,
            read_at
          )
        `)
        .eq('conversation_id', conversationId)
        .eq('organization_id', convData.organization_id)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('Error fetching messages:', messagesError);
        throw messagesError;
      }

      // For messages with sender_type = 'parent', fetch parent information separately
      // For messages with sender_type = 'staff', fetch organization information
      const processedMessages = await Promise.all(
        messagesData.map(async (message) => {
          let senderInfo = null;
          if (message.sender_type === 'parent') {
            const { data: parentData, error: parentError } = await supabase
              .from('parents')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();

            if (!parentError) {
              senderInfo = parentData;
            }
          } else if (message.sender_type === 'staff') {
            // For staff messages, get organization name instead of individual staff name
            const { data: orgData, error: orgError } = await supabase
              .from('organizations')
              .select('name')
              .eq('id', convData.organization_id)
              .single();

            if (!orgError) {
              senderInfo = { name: orgData.name };
            }
          }

          // Check if current user's message has been read
          const isCurrentUserMessage = message.sender_id === user?.id;
          const hasBeenRead = isCurrentUserMessage && 
            message.message_reads && 
            message.message_reads.length > 0;

          return {
            ...message,
            parent: message.sender_type === 'parent' ? senderInfo : null,
            organization: message.sender_type === 'staff' ? senderInfo : null,
            is_read: hasBeenRead,
            read_at: hasBeenRead && message.message_reads[0]?.read_at || null
          };
        })
      );

      setMessages(processedMessages);
      
      // Mark unread messages as read
      if (user?.id) {
        const unreadMessages = messagesData.filter(msg => {
          // Check if this message is not from the current user
          if (msg.sender_id === user.id) return false;
          
          // Check if there's a read record for this message by this user
          return !msg.message_reads?.some(read => read.user_id === user.id);
        });

        if (unreadMessages.length > 0) {
          const reads = unreadMessages.map(msg => ({
            message_id: msg.id,
            user_id: user.id
          }));

          await supabase.from('message_reads').insert(reads);
          
          // Update conversations queries to reflect read status
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    // Trigger typing indicator
    if (typingIndicator) {
      typingIndicator();
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !user || sendingMessage) return;

    try {
      setSendingMessage(true);
      
      // Get the organization_id for the message
      const { data: conversationData, error: convError } = await supabase
        .from('conversations')
        .select('organization_id')
        .eq('id', conversationId)
        .single();
      
      if (convError) {
        console.error('Error fetching conversation:', convError);
        throw convError;
      }
      
      const organizationId = conversationData?.organization_id;
      
      if (!organizationId) {
        toast.error('Organization data not available');
        return;
      }

      // Determine sender_type based on user role
      const getSenderType = (userRole: string) => {
        if (["bizadmin", "owner", "staff"].includes(userRole)) return "staff";
        if (userRole === "parent") return "parent";
        return "staff"; // fallback
      };
      const senderType = getSenderType(user?.user_metadata?.role);

      // Use the sendMessage service function to ensure consistent saving
      const sentMessage = await sendMessage(
        conversationId, 
        user.id, 
        senderType,
        newMessage.trim(),
        organizationId
      );

      if (!sentMessage) {
        throw new Error('Failed to send message');
      }

      // Clear the input field
      setNewMessage('');
      
      // Invalidate conversation queries to update the conversation list
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      
      // Auto-scroll to new message
      setTimeout(() => scrollToBottom(), 100);
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = (timestamp: string) => {
    return formatMessageTime(timestamp);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const cursorPosition = inputRef.current?.selectionStart || newMessage.length;
    const updatedMessage = 
      newMessage.substring(0, cursorPosition) + 
      emoji + 
      newMessage.substring(cursorPosition);
    
    setNewMessage(updatedMessage);
    
    // Focus back on input after selecting emoji
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.selectionStart = cursorPosition + emoji.length;
        inputRef.current.selectionEnd = cursorPosition + emoji.length;
      }
    }, 10);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-full">Loading messages...</div>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
            <Users className="h-12 w-12 text-muted-foreground/30" />
            <div className="font-semibold text-lg mb-1">No messages yet</div>
            <div className="text-sm">Start a conversation!</div>
          </div>
        ) : (
          messages.map((message, idx) => {
            const isSelf = message.sender_id === user?.id;
            const showAvatar = !isSelf && (idx === 0 || messages[idx - 1].sender_id !== message.sender_id);
            return (
              <div
                key={message.id}
                className={`flex items-end gap-2 group ${isSelf ? 'justify-end' : 'justify-start'}`}
                tabIndex={0}
                aria-label={isSelf ? 'Your message' : `Message from ${message.parent ? message.parent.first_name : 'User'}`}
              >
                {/* Avatar for others, only on first in group or on hover */}
                {!isSelf && (
                  <div className={`transition-opacity duration-200 ${showAvatar ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                    <EnhancedAvatar
                      firstName={message.parent?.first_name}
                      lastName={message.parent?.last_name}
                      userName={message.parent ? 
                        [message.parent.first_name, message.parent.last_name]
                          .filter(Boolean)
                          .join(' ') || 'Parent' 
                        : 'Parent'
                      }
                      size="sm"
                    />
                  </div>
                )}
                <div className={`flex flex-col max-w-[75%] ${isSelf ? 'items-end' : 'items-start'}`}>
                  {/* Sender name for others */}
                  {!isSelf && (
                    <div className="text-xs font-semibold mb-1 text-muted-foreground/80">
                      {message.sender_type === 'parent' && message.parent && (
                        `${message.parent.first_name} ${message.parent.last_name}`
                      )}
                      {message.sender_type === 'staff' && message.organization && (
                        message.organization.name
                      )}
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2 shadow-md transition-all animate-fade-in
                      ${isSelf ? 'bg-blue-500 text-white rounded-br-md shadow-lg' : 'bg-white/80 backdrop-blur-sm border border-white/30 rounded-bl-md shadow-lg'}
                    `}
                  >
                    <span className="whitespace-pre-line break-words">{message.content}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs mt-1 text-muted-foreground/60">
                    <span>{formatTime(message.created_at)}</span>
                    {isSelf && (
                      <span className="flex items-center ml-1" title={message.is_read ? 'Read' : 'Delivered'}>
                        {message.is_read 
                          ? <CheckCheck className="h-3 w-3 ml-1" /> 
                          : <Check className="h-3 w-3 ml-1" />
                        }
                      </span>
                    )}
                  </div>
                </div>
                {/* Avatar for self (optional, right side) */}
                {/* {isSelf && <div className="w-8" />} */}
              </div>
            );
          })
        )}
        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 mt-2 ml-2">
            <EnhancedAvatar 
              userName="Typing"
              size="sm" 
            />
            <div className="flex gap-1">
              <span className="inline-block w-2 h-2 bg-muted rounded-full animate-bounce [animation-delay:0ms]"></span>
              <span className="inline-block w-2 h-2 bg-muted rounded-full animate-bounce [animation-delay:150ms]"></span>
              <span className="inline-block w-2 h-2 bg-muted rounded-full animate-bounce [animation-delay:300ms]"></span>
            </div>
            <span className="text-xs text-muted-foreground">{typingUsers.length === 1 ? 'Someone is typing...' : 'Multiple people are typing...'}</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="sticky bottom-0 p-4 border-t bg-card z-10 shadow-lg">
        <div className="flex gap-2 items-center">
          <input
            ref={inputRef}
            type="text"
            className="border rounded-full flex-grow px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            placeholder="Type your message..."
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            aria-label="Type your message"
          />
          {/* Emoji Picker with Popover - always visible */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                title="Add emoji"
                aria-label="Add emoji"
              >
                <SmilePlus className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="end">
              <Suspense fallback={<div style={{ width: 300, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>}>
                <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
              </Suspense>
            </PopoverContent>
          </Popover>
          <Button 
            onClick={handleSend} 
            disabled={(!newMessage.trim()) || sendingMessage}
            className="px-4"
            aria-label="Send message"
          >
            {sendingMessage ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <SendHorizontal className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessageThread;
